import { useState, useEffect } from "react";
import { Copy, Check, ExternalLink } from "lucide-react";
import EndpointFilters from "@/components/common/EndpointFilters";
import { brandRegions, useBranding } from "../../contexts/BrandingContext";
import {
  DEFAULT_POP_FORM,
  popFormErrors,
  popFormFromSettings,
  popSettingsPayload,
} from "./popSettingsForm";

const KIND_OPTIONS = [
  { label: "Dynamic", bit: 1 },
  { label: "Overlay", bit: 2 },
  { label: "Banner", bit: 4 },
  { label: "Static", bit: 8 },
];

const TYPE_OPTIONS = [
  { value: "vast", label: "VAST" },
  { value: "rtb", label: "RTB" },
];

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "paused", label: "Paused" },
];

const PRICING_OPTIONS = [
  {
    value: "profit_share",
    label: "Profit share (50/50)",
    hint: "We bid the publisher's floor plus half the profit left after the DSP endpoint's margin (RTB default).",
  },
  {
    value: "fixed_cpm",
    label: "Fixed CPM",
    hint: "We pay this publisher a fixed CPM per impression (VAST).",
  },
  {
    value: "revshare",
    label: "Revenue share",
    hint: "We pay this publisher a share of whatever the DSP clears at (RTB).",
  },
];

const DEFAULT_FORM = {
  name: "",
  alias: "",
  types: ["vast"],
  kinds: 0,
  cpm: 0,
  allowTransit: false,
  status: "active",
  maxIncomingQps: "",
  pricingModel: "profit_share",
  revSharePct: "",
  filters: null,
  companyName: "",
  settings: {
    transitBufferMs: "",
    issueViewerCookie: false,
    rawSamplePct: "",
  },
  pop: { ...DEFAULT_POP_FORM },
};

const API_PREFIX = "/api";
const API_BASE = import.meta.env?.VITE_API_URL ?? API_PREFIX;

const buildServingUrl = (protocol, alias, token, origin) => {
  // Publisher serving URLs live at the HOST ROOT, never under /api: nginx routes
  // /vast and /rtb straight to the serving (app) pool, whereas /api/* goes to the
  // single api replica. Always strip a trailing /api (both the bare "/api" default
  // AND a full "https://host/api" base) so the copyable URL is host-root
  // /vast|/rtb — the canonical path publishers should point their players at.
  let base = API_BASE.replace(/\/+$/, "");
  if (base.endsWith(API_PREFIX)) {
    base = base.slice(0, -API_PREFIX.length);
  }
  // `alias` is empty for links whose path is just protocol + token (the public
  // statistics link): skip the empty segment rather than emitting a double slash.
  const path = [base, protocol, alias, token].filter(Boolean).join("/");
  try {
    return new URL(path, origin || window.location.origin).href;
  } catch {
    return path;
  }
};

const slugify = (value) =>
  value
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");

const fromProvider = (p) => ({
  name: p.name ?? "",
  alias: p.alias ?? "",
  types: Array.isArray(p.types) && p.types.length ? p.types : ["vast"],
  kinds: p.kinds ?? 0,
  cpm: p.cpm ?? 0,
  allowTransit: p.allowTransit ?? false,
  status: p.status ?? "active",
  maxIncomingQps: p.maxIncomingQps != null ? String(p.maxIncomingQps) : "",
  pricingModel: p.pricingModel ?? "profit_share",
  revSharePct: p.revSharePct != null ? String(p.revSharePct) : "",
  filters: p.filters ?? null,
  companyName: p.companyName ?? "",
  settings: {
    transitBufferMs:
      p.settings?.transitBufferMs != null
        ? String(p.settings.transitBufferMs)
        : "",
    issueViewerCookie: Boolean(p.settings?.issueViewerCookie),
    rawSamplePct:
      p.settings?.rawSamplePct != null ? String(p.settings.rawSamplePct) : "",
  },
  pop: popFormFromSettings(p.settings?.pop),
});

// Popunder buying is offered on rtb publishers that do not relay transit.
const canPop = (form) => form.types.includes("rtb") && !form.allowTransit;

function ServingUrl({ label, url }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      window.prompt("Copy this URL", url);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="min-w-[72px] text-xs font-bold uppercase text-(--text-muted)">
        {label}
      </span>
      <code className="flex-1 truncate rounded-(--radius-sm) bg-(--surface-2) px-2 py-1.5 text-xs text-(--text-primary)">
        {url}
      </code>
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex items-center justify-center rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-2) p-1.5 text-(--text-secondary) transition-colors hover:bg-(--surface-muted)"
        title="Copy URL"
        aria-label={`Copy ${label} URL`}
      >
        {copied ? (
          <Check size={15} className="text-(--status-success)" />
        ) : (
          <Copy size={15} />
        )}
      </button>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center rounded-(--radius-sm) border border-(--border-subtle) bg-(--surface-2) p-1.5 text-(--text-secondary) transition-colors hover:bg-(--surface-muted)"
        title="Open URL"
        aria-label={`Open ${label} URL`}
      >
        <ExternalLink size={15} />
      </a>
    </div>
  );
}

// A protocol's publisher URL, rendered as one copyable row per region. Regions
// are server-provided per instance (apps/backend/branding/<tenant>/brand.json):
// GLOBAL = the current host, plus any regional nodes serving this instance's data.
function ServingUrlGroup({ title, protocol, alias, token }) {
  const regions = brandRegions(useBranding());
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11px] font-bold uppercase tracking-wide text-(--text-muted)">
        {title}
      </span>
      <div className="flex flex-col gap-2">
        {regions.map((region) => (
          <ServingUrl
            key={region.label}
            label={region.label}
            url={buildServingUrl(protocol, alias, token, region.origin)}
          />
        ))}
      </div>
    </div>
  );
}

export default function ProviderForm({ initialProvider, onSubmit, onCancel }) {
  const [form, setForm] = useState(() =>
    initialProvider ? fromProvider(initialProvider) : { ...DEFAULT_FORM },
  );
  const [error, setError] = useState("");

  useEffect(() => {
    setForm(
      initialProvider ? fromProvider(initialProvider) : { ...DEFAULT_FORM },
    );
    setError("");
  }, [initialProvider]);

  const toggleKind = (bit) => {
    setForm((prev) => ({ ...prev, kinds: prev.kinds ^ bit }));
  };

  const toggleType = (value) => {
    setForm((prev) => {
      const nextTypes = prev.types.includes(value)
        ? prev.types.filter((t) => t !== value)
        : [...prev.types, value];
      return {
        ...prev,
        types: nextTypes,
        allowTransit: !nextTypes.includes("rtb") ? false : prev.allowTransit,
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Name is required");
      return;
    }
    if (form.types.length === 0) {
      setError("Select at least one type");
      return;
    }
    if (form.pricingModel === "revshare" && form.revSharePct === "") {
      setError("Revenue share % is required for revenue-share pricing");
      return;
    }
    const popForm = canPop(form)
      ? form.pop
      : { ...form.pop, configured: false };
    const popErrors = popFormErrors(popForm);
    if (popErrors.length > 0) {
      setError(popErrors[0]);
      return;
    }
    setError("");
    const hadPop = initialProvider?.settings?.pop != null;
    const popPayload = popSettingsPayload(popForm, hadPop);
    // Removing a stored pop config strands the pop campaigns that target this
    // publisher (they can no longer be saved): ask first.
    if (
      hadPop &&
      popPayload === null &&
      typeof window !== "undefined" &&
      !window.confirm(
        "This removes the publisher's popunder settings. Popunder campaigns targeting it will stop bidding here and must be edited. Continue?",
      )
    ) {
      return;
    }
    const hasRtb = form.types.includes("rtb");
    const hasVast = form.types.includes("vast");
    onSubmit({
      name: form.name.trim(),
      alias: form.alias.trim() || null,
      types: form.types,
      kinds: form.kinds,
      cpm: Number(form.cpm) || 0,
      allowTransit: hasRtb ? form.allowTransit : false,
      status: form.status,
      maxIncomingQps:
        form.maxIncomingQps !== "" ? Number(form.maxIncomingQps) : null,
      pricingModel: form.pricingModel,
      revSharePct:
        form.pricingModel === "revshare" && form.revSharePct !== ""
          ? Number(form.revSharePct)
          : null,
      filters: form.filters,
      companyName: form.companyName.trim() || null,
      settings: {
        transitBufferMs:
          hasRtb && form.settings.transitBufferMs !== ""
            ? Number(form.settings.transitBufferMs)
            : null,
        issueViewerCookie: hasVast
          ? Boolean(form.settings.issueViewerCookie)
          : false,
        rawSamplePct:
          hasRtb && form.settings.rawSamplePct !== ""
            ? Number(form.settings.rawSamplePct)
            : null,
        ...(popPayload !== undefined ? { pop: popPayload } : {}),
      },
    });
  };

  const savedAlias = initialProvider?.alias ?? "";
  const savedToken = initialProvider?.secretToken ?? "";
  const savedTypes = Array.isArray(initialProvider?.types)
    ? initialProvider.types
    : [];
  const canShowUrls = Boolean(savedAlias && savedToken);
  const showVastUrl = canShowUrls && savedTypes.includes("vast");
  const showRtbUrl = canShowUrls && savedTypes.includes("rtb");

  return (
    <form
      className="builder-form"
      style={{ gap: "0.5rem" }}
      onSubmit={handleSubmit}
    >
      {error && (
        <div className="form-error" style={{ marginBottom: "1rem" }}>
          {error}
        </div>
      )}

      <div className="form-group-modal">
        <label htmlFor="provider-name">Name *</label>
        <input
          id="provider-name"
          value={form.name}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, name: e.target.value }))
          }
          placeholder="e.g. Acme Ad Network"
          required
        />
      </div>

      <div className="form-group-modal">
        <label htmlFor="provider-alias">Alias</label>
        <input
          id="provider-alias"
          value={form.alias}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, alias: slugify(e.target.value) }))
          }
          placeholder="e.g. acme-network"
        />
        <p className="form-help-text">
          URL-safe identifier used in serving links (lowercase letters, numbers,
          hyphens).
        </p>
      </div>

      <div className="builder-grid two-columns">
        <div className="form-group-modal">
          <label htmlFor="provider-status">Status</label>
          <select
            id="provider-status"
            value={form.status}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, status: e.target.value }))
            }
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <p className="form-help-text">
            Paused stops this endpoint at once, with no release: VAST returns an
            empty document and RTB returns a no-bid.
          </p>
        </div>
        <div className="form-group-modal">
          <label htmlFor="provider-company">Company</label>
          <input
            id="provider-company"
            value={form.companyName}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, companyName: e.target.value }))
            }
            placeholder="Owning company"
          />
        </div>
      </div>

      <div className="form-group-modal">
        <label>Type</label>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            marginTop: "0.25rem",
          }}
        >
          {TYPE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={form.types.includes(opt.value)}
                onChange={() => toggleType(opt.value)}
              />
              <span>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {form.types.includes("rtb") && (
        <div className="form-group-modal">
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={form.allowTransit}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, allowTransit: e.target.checked }))
              }
            />
            <span>Allow transit campaigns</span>
          </label>
          <p className="form-help-text">
            Enables this publisher to participate as a transit endpoint.
          </p>
        </div>
      )}

      {canPop(form) && (
        <PopSettingsSection
          pop={form.pop}
          onChange={(patch) =>
            setForm((prev) => ({ ...prev, pop: { ...prev.pop, ...patch } }))
          }
        />
      )}

      {form.types.includes("rtb") && (
        <div className="form-group-modal">
          <label htmlFor="provider-transit-buffer">
            Transit await buffer (ms)
          </label>
          <input
            id="provider-transit-buffer"
            type="number"
            min="0"
            step="1"
            value={form.settings.transitBufferMs}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                settings: { ...prev.settings, transitBufferMs: e.target.value },
              }))
            }
            placeholder="e.g. 200"
          />
          <p className="form-help-text">
            Optional. Maximum time the transit layer waits before forwarding the
            bid request.
          </p>
        </div>
      )}

      {form.types.includes("vast") && (
        <div className="form-group-modal">
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.4rem",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={form.settings.issueViewerCookie}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    issueViewerCookie: e.target.checked,
                  },
                }))
              }
            />
            <span>Issue web-viewer cookie (frequency capping)</span>
          </label>
          <p className="form-help-text">
            Issues a partitioned viewer cookie (fingerprint fallback) so web
            viewers can be frequency-capped. Requires the global feature flag.
          </p>
        </div>
      )}

      {(showVastUrl || showRtbUrl) && (
        <div className="form-group-modal">
          <label>Serving URLs</label>
          <div className="flex flex-col gap-2">
            {showVastUrl && (
              <ServingUrlGroup
                title="vast"
                protocol="vast"
                alias={savedAlias}
                token={savedToken}
              />
            )}
            {showRtbUrl && (
              <ServingUrlGroup
                title="rtb"
                protocol="rtb"
                alias={savedAlias}
                token={savedToken}
              />
            )}
          </div>
        </div>
      )}

      {canShowUrls && (
        <div className="form-group-modal">
          <label>Reporting URL</label>
          <div className="flex flex-col gap-2">
            <ServingUrlGroup
              title="stats"
              protocol="report/public/ssp"
              alias=""
              token={savedToken}
            />
          </div>
          <p className="form-help-text">
            Read-only statistics for this publisher, with no login and no
            headers. Append{" "}
            <code>
              ?date_from=YYYY-MM-DD&amp;date_to=YYYY-MM-DD&amp;group_by=day
            </code>
            ; <code>group_by</code> accepts total, hour, day, week or month and{" "}
            <code>timezone</code> defaults to UTC.
          </p>
        </div>
      )}

      <div className="form-group-modal">
        <label>Ad kinds</label>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.75rem",
            marginTop: "0.25rem",
          }}
        >
          {KIND_OPTIONS.map(({ label, bit }) => (
            <label
              key={bit}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.4rem",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={(form.kinds & bit) !== 0}
                onChange={() => toggleKind(bit)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="form-group-modal">
        <label htmlFor="provider-pricing">Pricing model</label>
        <select
          id="provider-pricing"
          value={form.pricingModel}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, pricingModel: e.target.value }))
          }
        >
          {PRICING_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <p className="form-help-text">
          {PRICING_OPTIONS.find((o) => o.value === form.pricingModel)?.hint}
        </p>
      </div>

      {form.pricingModel === "revshare" && (
        <div className="form-group-modal">
          <label htmlFor="provider-revshare">Revenue share (%)</label>
          <input
            id="provider-revshare"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={form.revSharePct}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, revSharePct: e.target.value }))
            }
            placeholder="e.g. 80"
          />
          <p className="form-help-text">
            Share of the winning DSP bid paid to this publisher. At 80% a $10
            bid pays them $8 and we keep $2.
          </p>
        </div>
      )}

      <div className="form-group-modal">
        <label htmlFor="provider-qps">Max incoming QPS</label>
        <input
          id="provider-qps"
          type="number"
          min="1"
          step="1"
          value={form.maxIncomingQps}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, maxIncomingQps: e.target.value }))
          }
          placeholder="Leave empty for no limit"
        />
        <p className="form-help-text">
          Requests per second accepted from this publisher. Excess requests get
          a controlled no-bid and are counted, never an error. Changes take
          effect immediately.
        </p>
      </div>

      <div className="form-group-modal">
        <label>Routing filters</label>
        <EndpointFilters
          value={form.filters}
          onChange={(filters) => setForm((prev) => ({ ...prev, filters }))}
          description="Which incoming requests this publisher may send us. A request must pass every active filter."
        />
      </div>

      {form.types.includes("rtb") && (
        <div className="form-group-modal">
          <label htmlFor="provider-raw-sample">Debug capture (%)</label>
          <input
            id="provider-raw-sample"
            type="number"
            min="0"
            max="100"
            step="1"
            value={form.settings.rawSamplePct}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                settings: { ...prev.settings, rawSamplePct: e.target.value },
              }))
            }
            placeholder="Default 1"
          />
          <p className="form-help-text">
            Share of bid requests whose full body and per-DSP call trace are
            kept for debugging. Every request we actually bid on is kept
            regardless. 0 turns sampling off.
          </p>
        </div>
      )}

      <div className="form-group-modal">
        <label htmlFor="provider-cpm">CPM ($)</label>
        <input
          id="provider-cpm"
          type="number"
          min="0"
          step="0.01"
          value={form.cpm}
          onChange={(e) =>
            setForm((prev) => ({
              ...prev,
              cpm: e.target.value,
            }))
          }
          placeholder="e.g. 2.50"
        />
        <p className="form-help-text">
          The fixed buy CPM we pay for this publisher&apos;s inventory. Used for
          VAST serving and, on fixed-CPM pricing, for RTB too.
        </p>
      </div>

      <div
        style={{
          display: "flex",
          gap: "0.75rem",
          justifyContent: "flex-end",
          marginTop: "1.5rem",
        }}
      >
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {initialProvider ? "Save Changes" : "Create Publisher"}
        </button>
      </div>
    </form>
  );
}

const checkboxRow = {
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  cursor: "pointer",
};

// Popunder buying on this publisher (providers.settings.pop). "Enabled" is the
// only bidding switch.
function PopSettingsSection({ pop, onChange }) {
  return (
    <div className="form-group-modal rounded-(--radius-md) border border-(--border-subtle) p-3">
      <label style={checkboxRow}>
        <input
          type="checkbox"
          checked={pop.configured}
          onChange={(e) => onChange({ configured: e.target.checked })}
        />
        <span>Popunder buying</span>
      </label>
      <p className="form-help-text">
        Lets popunder campaigns target this publisher. Bidding starts only when
        it is enabled here and globally.
      </p>
      {pop.configured && (
        <div className="mt-3 flex flex-col gap-3">
          <label style={checkboxRow}>
            <input
              type="checkbox"
              checked={pop.firstPriceConfirmed}
              onChange={(e) =>
                onChange(
                  e.target.checked
                    ? { firstPriceConfirmed: true }
                    : { firstPriceConfirmed: false, enabled: false },
                )
              }
            />
            <span>
              Partner confirmed first-price with a clear-text $
              {"{AUCTION_PRICE}"}
            </span>
          </label>
          <label style={checkboxRow}>
            <input
              type="checkbox"
              checked={pop.enabled}
              disabled={!pop.firstPriceConfirmed}
              onChange={(e) => onChange({ enabled: e.target.checked })}
            />
            <span>Bid on pop traffic from this publisher</span>
          </label>
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label htmlFor="pop-adm">Ad markup</label>
              <select
                id="pop-adm"
                value={pop.admFormat}
                onChange={(e) => onChange({ admFormat: e.target.value })}
              >
                <option value="url">Redirect URL</option>
                <option value="exads_xml">EXADS popunder XML</option>
              </select>
            </div>
            <div>
              <label htmlFor="pop-cost-on">Billing event (our cost)</label>
              <select
                id="pop-cost-on"
                value={pop.costOn}
                onChange={(e) => onChange({ costOn: e.target.value })}
              >
                <option value="burl">burl (billing notice)</option>
                <option value="nurl">nurl (win notice)</option>
              </select>
            </div>
            <div>
              <label htmlFor="pop-ttl">Redirect lifetime (s)</label>
              <input
                id="pop-ttl"
                type="number"
                min="60"
                max="1800"
                value={pop.redirectTtlSec}
                onChange={(e) => onChange({ redirectTtlSec: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="pop-failure">On a failed notice write</label>
              <select
                id="pop-failure"
                value={pop.noticeFailure}
                onChange={(e) => onChange({ noticeFailure: e.target.value })}
              >
                <option value="retry">503 — partner retries</option>
                <option value="ack">204 — partner never retries</option>
              </select>
            </div>
          </div>
          <div>
            <label htmlFor="pop-fallback">Fallback URL (capped visits)</label>
            <input
              id="pop-fallback"
              value={pop.fallbackUrl}
              onChange={(e) => onChange({ fallbackUrl: e.target.value })}
              placeholder="Empty ⇒ 204"
            />
          </div>
          <div>
            <label htmlFor="pop-allowlist">Notice IP allowlist (CIDRs)</label>
            <textarea
              id="pop-allowlist"
              rows={2}
              value={pop.noticeIpAllowlist}
              onChange={(e) => onChange({ noticeIpAllowlist: e.target.value })}
              placeholder="One per line; empty ⇒ not checked"
            />
          </div>
          <div>
            <label htmlFor="pop-zones">Zone id fields (in order)</label>
            <input
              id="pop-zones"
              value={pop.zoneFields}
              onChange={(e) => onChange({ zoneFields: e.target.value })}
            />
          </div>
          <label style={checkboxRow}>
            <input
              type="checkbox"
              checked={pop.sendBtype}
              onChange={(e) => onChange({ sendBtype: e.target.checked })}
            />
            <span>Send ext.btype = 1 (EXADS)</span>
          </label>
          <label style={checkboxRow}>
            <input
              type="checkbox"
              checked={pop.requireSchain}
              onChange={(e) => onChange({ requireSchain: e.target.checked })}
            />
            <span>Require a supply chain (schain)</span>
          </label>
        </div>
      )}
    </div>
  );
}
